import { useState } from 'react';

import type { LucideIcon } from 'lucide-react';

import { useHoldAction } from '@/hooks';

import { radialPadColors, radialPadLayout } from '../radialControlPad.constants.ts';
import {
  RadialSectorIconWrapper,
  RadialSectorPath,
} from './radialSectorButton/radialSectorButton.style.ts';

type RadialSectorButtonProps = {
  /** SVG path for the clickable pie sector (from `slicePath`). */
  d: string;
  icon: LucideIcon;
  /** Accessible name for the sector; also used as tooltip text in {@link RadialSectorTooltip}. */
  label: string;
  /** Icon center in SVG coordinates (same space as `d`). */
  iconX: number;
  iconY: number;
  iconSize?: number;
  iconBox?: number;
  disabled?: boolean;
  /** Interval between repeated `onStart` calls while held (see `useHoldAction`). */
  holdRepeatDelayMs?: number;
  /** Fired on pointer down; repeated while held until pointer up. */
  onStart: () => void;
  /** Fired on pointer up, cancel, blur, or tab hidden. */
  onStop: () => void;
  /** Optional hover callbacks; drives paired {@link RadialSectorTooltip} via the ring builder. */
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
};

/**
 * Single pie-sector control in a {@link RadialControlPad} ring.
 *
 * Renders an SVG sector path for hit-testing and hold-to-repeat input, plus a
 * centered Lucide icon via `foreignObject`. Pointer events on the path drive
 * `onStart` / `onStop`; the icon layer does not receive pointer events.
 */
export const RadialSectorButton = ({
  d,
  icon: Icon,
  label,
  iconX,
  iconY,
  iconSize = radialPadLayout.icon.innerSize,
  iconBox = radialPadLayout.icon.box,
  disabled,
  holdRepeatDelayMs,
  onStart,
  onStop,
  onPointerEnter,
  onPointerLeave,
}: RadialSectorButtonProps) => {
  const [hovered, setHovered] = useState(false);
  const { active, handlePointerDown, handlePointerUp, handlePointerCancel } = useHoldAction({
    disabled,
    holdRepeatDelayMs,
    onStart,
    onStop,
  });

  const iconColor = resolveSectorIconColor(disabled, active, hovered);

  return (
    <g>
      <RadialSectorPath
        d={d}
        role='button'
        tabIndex={disabled ? -1 : 0}
        aria-label={label}
        aria-pressed={active}
        $active={active}
        $disabled={disabled}
        onPointerEnter={() => {
          setHovered(true);
          onPointerEnter?.();
        }}
        onPointerLeave={() => {
          setHovered(false);
          onPointerLeave?.();
        }}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onContextMenu={e => e.preventDefault()}
      />
      <foreignObject
        x={iconX - iconBox / 2}
        y={iconY - iconBox / 2}
        width={iconBox}
        height={iconBox}
        pointerEvents='none'
      >
        <RadialSectorIconWrapper>
          <Icon size={iconSize} strokeWidth={1.75} aria-hidden color={iconColor} />
        </RadialSectorIconWrapper>
      </foreignObject>
    </g>
  );
};

const resolveSectorIconColor = (
  disabled: boolean | undefined,
  active: boolean,
  hovered: boolean
) => {
  const icon = radialPadColors.sector.icon;
  if (disabled) {
    return icon.disabled;
  }
  if (active) {
    return icon.pressed;
  }
  if (hovered) {
    return icon.hover;
  }
  return icon.default;
};
